// Reads the founder's onboarding questionnaire (the m8t_onboarding block) from the
// Foundry conversation data plane and renders a brain company-profile.md + its index line.

export interface OnboardingBlock {
  schema_version?: string;
  company_stage: string;
  icp?: string;
  industry?: string;
  team_size?: string;
  context: string;
}

export const COMPANY_PROFILE_PATH = "memory/company-profile.md";

export const DEFAULT_MEMORY_INDEX_HEADER = [
  `# Memory index`,
  ``,
  `> Your memories, newest first. The summary on each line is usually enough to answer — open the linked file only when you need full detail. Each path is repo-root; copy it verbatim, never invent one.`,
  ``,
].join("\n");

/** Walk fenced JSON blocks (newest first), then the raw text, for a valid m8t_onboarding block. */
export function parseOnboardingBlock(text: string): OnboardingBlock | null {
  const candidates = [
    ...[...text.matchAll(/```(?:json)?\s*([\s\S]*?)```/g)].map((m) => m[1]),
    text,
  ].reverse();
  for (const c of candidates) {
    try {
      const o = (JSON.parse(c.trim()) as { m8t_onboarding?: OnboardingBlock }).m8t_onboarding;
      if (o?.company_stage && o.context) return o;
    } catch {
      // not valid JSON — skip
    }
  }
  return null;
}

async function safeJson(res: { text(): Promise<string> }): Promise<unknown> {
  try { const t = await res.text(); return t ? JSON.parse(t) : null; } catch { return null; }
}

export interface FindProfileResult { hadIntake: boolean; block: OnboardingBlock | null }

/**
 * List the founder's conversations, keep the stacey-intake ones (the webapp tags every
 * conversation metadata.agent="stacey-intake"), newest first, and scan each one's /items
 * for the onboarding block. `/items` (not `/messages`) is where the message text lives.
 */
export async function findOnboardingProfile(args: {
  endpoint: string; token: string; fetchImpl?: typeof fetch;
}): Promise<FindProfileResult> {
  const doFetch = args.fetchImpl ?? fetch;
  const H = { Authorization: `Bearer ${args.token}` };
  const listRes = await doFetch(`${args.endpoint}/conversations?api-version=v1&order=desc&limit=20`, { headers: H });
  const list = (await safeJson(listRes)) as { data?: { id: string; metadata?: { agent?: string } }[] } | null;
  const convs = (list?.data ?? []).filter((c) => c.metadata?.agent === "stacey-intake");
  for (const conv of convs) {
    const itemsRes = await doFetch(`${args.endpoint}/conversations/${conv.id}/items?api-version=v1`, { headers: H });
    const items = (await safeJson(itemsRes)) as { data?: { content?: { text?: string }[] }[] } | null;
    const text = (items?.data ?? []).flatMap((m) => (m.content ?? []).map((c) => c.text ?? "")).join("\n");
    const block = parseOnboardingBlock(text);
    if (block) return { hadIntake: true, block };
  }
  return { hadIntake: convs.length > 0, block: null };
}

export function renderCompanyProfile(
  block: OnboardingBlock, now: string = new Date().toISOString(),
): { profileMd: string; memoryIndexLine: string } {
  const dash = (v?: string) => (v?.trim() ? v.trim() : "—");
  const profileMd = [
    `---`,
    `type: memory`,
    `title: Company profile`,
    `created: ${now}`,
    `updated: ${now}`,
    `tags: [company-profile, onboarding, founder]`,
    `origin: operator`,
    `---`,
    ``,
    `# Company profile`,
    ``,
    `_Seeded from the onboarding questionnaire._`,
    ``,
    `- **Stage:** ${dash(block.company_stage)}`,
    `- **ICP:** ${dash(block.icp)}`,
    `- **Industry:** ${dash(block.industry)}`,
    `- **Team size:** ${dash(block.team_size)}`,
    ``,
    `## Context`,
    ``,
    block.context.trim(),
    ``,
  ].join("\n");
  const bits = [block.company_stage.trim(), block.industry?.trim(), block.icp?.trim() ? `ICP ${block.icp.trim()}` : undefined]
    .filter(Boolean).join(" · ");
  const memoryIndexLine = `- \`${COMPANY_PROFILE_PATH}\` — **Company profile**: ${bits}. (seeded from onboarding)`;
  return { profileMd, memoryIndexLine };
}

/** Compare two rendered company-profile docs ignoring the volatile created/updated lines. */
export function companyProfileBodyEquals(a: string, b: string): boolean {
  const strip = (s: string) => s.replace(/^(created|updated):.*$/gm, "").trim();
  return strip(a) === strip(b);
}

/** Prepend the company-profile index line above the first list item, replacing any prior one. */
export function upsertMemoryIndex(existing: string, line: string): string {
  const lines = existing.split("\n");
  const existingIdx = lines.findIndex((l) => l.includes(`\`${COMPANY_PROFILE_PATH}\``));
  if (existingIdx >= 0) {
    lines[existingIdx] = line;
    return lines.join("\n");
  }
  const firstItemIdx = lines.findIndex((l) => /^-\s+`memory\//.test(l));
  if (firstItemIdx >= 0) {
    lines.splice(firstItemIdx, 0, line);
    return lines.join("\n");
  }
  return existing.replace(/\s*$/, "\n\n") + line + "\n";
}
